/* eslint-disable ember/no-get, ember/no-new-mixins, ember/no-runloop, no-unused-vars, warp-drive/no-legacy-request-patterns */
import Mixin from '@ember/object/mixin';
import { A } from '@ember/array';
import { action, computed } from '@ember/object';
import { service } from '@ember/service';
import { debounce } from '@ember/runloop';
import { hash } from '@linc-technologies/linced-common/utils/promise';
import ProgressionType from '@linc-technologies/linced-common/enums/progression-type';
import GoalState from '@linc-technologies/linced-common/enums/goal-state';
import Options from '@linc-technologies/linced-common/enums/options';
import { findBy } from '@linc-technologies/linced-common/utils/arrays';
import {
  COMPONENT_FIELD_MAP,
  hasText,
  isListed,
  listItemTexts,
  manualEditProperties,
  parseConfig,
  PROGRESS_COMPONENT_TYPES,
  saveSection,
  TEXT_COMPONENT_TYPES,
} from 'linced-app/utils/reporting';
import {
  applyRubricComments,
  canAutoPopulate,
  generateRubricCommentsForSubject,
} from 'linced-app/utils/rubric-comments';
import {
  getCurrentMilestone,
  generateExpectationData,
  generateProgressData,
} from '@linc-technologies/linced-common/utils/progressions';
import {
  resolveRubricElements,
  rubricElementTotals,
  sectionRubricFamilyId,
} from '@linc-technologies/linced-common/utils/rubrics';
import {
  remapSummariesByLearner,
  mapGradesByLearner,
} from '../utils/data-cards';
import { progressionsByStudent } from '../utils/progression-history';

export default Mixin.create({
  // Services
  clickToEditCoordinator: service(),
  dates: service(),
  fetch: service(),
  options: service(),
  session: service(),
  store: service(),
  toaster: service(),

  // Shared write state
  activeReport: null,
  activeSection: null,
  activeReportContentSections: null,
  studentReportSectionMap: null,
  studentReportMap: null,
  postCountMap: null,
  studentPostsMap: null,
  writeReportsOpen: false,

  // OTJ
  otjSubject: null,
  otjLevels: null,
  otjExpectations: null,
  otjProgressions: null,
  otjMilestoneMap: null,
  elementJudgementHistory: null,
  elementEffortHistory: null,

  // Goals
  goalsAllLevels: null,
  goalsCategories: null,
  goalsAllExpectations: null,
  goalsLearnerSummaries: null,
  goalsSubjects: null,
  goalsCurrentStatesMap: null,

  // Assessment
  assessmentAssessments: null,
  assessmentModules: null,
  assessmentGradesMap: null,

  // Per-panel triangulation load errors. Distinguish a failed load (flag true)
  // from "not configured" (data null, flag false) so the templates can show an
  // inline error in the affected panel instead of silently rendering nothing.
  otjLoadError: false,
  curriculumLoadError: false,
  currentGoalsLoadError: false,
  assessmentsLoadError: false,
  // The students last passed to _loadTriangulationData, so retryTriangulation
  // can re-run the load without the caller re-supplying them.
  _triangulationStudents: null,

  // Rubric
  showRubricDialog: false,
  rubricDialogStudent: null,

  // Add goals
  addGoalsOpen: false,
  addGoalsStudent: null,
  addGoalsSubject: null,
  addGoalsCategory: null,
  addGoalsItems: null,
  addGoalsSelected: null,

  COMPONENT_FIELD_MAP,

  activeSectionProgressComponents: computed(
    'activeSection.configuration',
    function () {
      let parsed = parseConfig(this.get('activeSection.configuration'));
      let components = parsed.components || [];
      return components.filter((c) =>
        PROGRESS_COMPONENT_TYPES.includes(c.type),
      );
    },
  ),

  activeSectionHasProgressPanel: computed(
    'activeSectionProgressComponents.[]',
    function () {
      return (this.activeSectionProgressComponents || []).length > 0;
    },
  ),

  activeSectionHasJudgementSlider: computed(
    'activeSectionProgressComponents.[]',
    function () {
      return (this.activeSectionProgressComponents || []).some(function (c) {
        return c.type === 'strand-visual' || c.type === 'progress-descriptor';
      });
    },
  ),

  activeSectionSubject: computed('activeSection.configuration', function () {
    let config = this.get('activeSection.configuration');
    if (!config) return null;
    let parsed = parseConfig(config);
    if (!parsed.subjectId) return null;
    return this.store.peekRecord('goals/subject', parsed.subjectId);
  }),

  activeSectionTextComponents: computed(
    'activeSection.configuration',
    'activeTextComponentTypes',
    function () {
      let config = this.get('activeSection.configuration');
      let parsed = parseConfig(config);
      let types = this.activeTextComponentTypes || TEXT_COMPONENT_TYPES;
      let components = parsed.components || [];
      return A(components.filter((c) => types.includes(c.type)));
    },
  ),

  activeSectionHasNarrative: computed(
    'activeSectionTextComponents.[]',
    function () {
      return (this.activeSectionTextComponents || []).some(
        (c) => c.type === 'teacher-comment',
      );
    },
  ),

  getStudentReportSection(studentId, templateSectionId) {
    let map = this.studentReportSectionMap;
    if (!map) return null;
    let studentSections = map[studentId];
    if (!studentSections) return null;
    return studentSections[templateSectionId] || null;
  },

  // The rubric family chosen on the active template section (null for the
  // default family).
  activeSectionRubricFamilyId: computed(
    'activeSection.configuration',
    function () {
      return sectionRubricFamilyId(this.activeSection);
    },
  ),

  _getActiveRubricElements(student) {
    return resolveRubricElements(
      this.activeSectionSubject,
      student,
      this.store,
      this.dates.rolloverMonthDate,
      this.activeSectionRubricFamilyId,
    );
  },

  // Sum a section's strand values for the student's current rubric. Values
  // stored against another rubric's elements (e.g. before the template's
  // rubric family changed) are ignored, so they don't skew the overall.
  _rubricElementTotals(elementProgressions, student, field) {
    return rubricElementTotals(
      elementProgressions,
      this._getActiveRubricElements(student),
      field,
    );
  },

  async _loadStudentPosts(students) {
    let section = this.activeSection;
    if (!section || !students || !students.length) {
      this.set('postCountMap', {});
      this.set('studentPostsMap', {});
      return;
    }

    let parsed = parseConfig(section.get('configuration'));
    let pageIds = parsed && parsed.triangulationPageIds;
    if (!pageIds || !pageIds.length) {
      this.set('postCountMap', {});
      this.set('studentPostsMap', {});
      return;
    }

    let countMap = {};
    let postsMap = {};
    let studentIds = students.map((s) => s.get('id'));
    try {
      let posts = await this.store.query('posts/post', {
        personIds: JSON.stringify(studentIds),
        pageIds: JSON.stringify(pageIds),
      });
      studentIds.forEach((id) => {
        countMap[id] = 0;
        postsMap[id] = [];
      });
      posts.forEach((post) => {
        let pid = post.belongsTo('personId').id();
        if (pid) {
          countMap[pid] = (countMap[pid] || 0) + 1;
          postsMap[pid] = (postsMap[pid] || []).concat(post);
        }
      });
    } catch (e) {
      studentIds.forEach((id) => {
        countMap[id] = 0;
        postsMap[id] = [];
      });
    }

    this.set('postCountMap', countMap);
    this.set('studentPostsMap', postsMap);
  },

  async _loadTriangulationData(students) {
    // Cache for retryTriangulation, and clear prior per-panel errors so a
    // fresh load starts from a clean slate (each loader re-flags on failure).
    this.set('_triangulationStudents', students);
    this.setProperties({
      otjLoadError: false,
      curriculumLoadError: false,
      currentGoalsLoadError: false,
      assessmentsLoadError: false,
    });

    let section = this.activeSection;
    if (!section) {
      this._clearTriangulationData();
      return;
    }

    // Capture the section id at load start so a stale load completing after
    // the user has switched sections doesn't auto-populate the new section's
    // next-steps with goals from the old section's triangulation subjects.
    let loadSectionId = section.get('id');
    let parsed = parseConfig(section.get('configuration'));
    let subjectId = parsed && parsed.subjectId;

    try {
      if (subjectId) {
        // Section subject set — load OTJ data plus the shared lookup data
        // (levels/expectations/categories) needed for the curriculum panel.
        // Isolate the OTJ load so its failure flags only the OTJ panel and
        // doesn't prevent goals/assessments below from loading.
        try {
          await this._loadSectionSubjectData(students, subjectId);
        } catch (e) {
          this._clearOtjData();
          this.set('otjLoadError', true);
        }
      } else {
        // No section subject — clear OTJ-specific state but still load the
        // shared lookup data so curriculum/goals/assessment panels can render.
        // The lookup feeds the curriculum panel, so isolate it and flag that
        // panel on failure rather than letting the outer catch silently hide
        // the whole column with no error indicator.
        this._clearOtjData();
        try {
          let [allLevels, allExpectations, categories] = await Promise.all([
            this.store.findAll('milestones/level'),
            this.store.findAll('milestones/expectation'),
            this.store.findAll('goals/category'),
          ]);
          this.setProperties({
            goalsAllLevels: allLevels,
            goalsCategories: categories,
            goalsAllExpectations: allExpectations,
          });
        } catch (e) {
          this.setProperties({
            goalsAllLevels: null,
            goalsCategories: null,
            goalsAllExpectations: null,
            curriculumLoadError: true,
          });
        }
      }

      // Load learner summaries (Curriculum + Current Goals) and assessment
      // data — these are configured via triangulationSubjectIds /
      // triangulationAssessmentIds independently of the section subject.
      await this._loadTriangulationGoals(students, parsed, loadSectionId);
      await this._loadTriangulationAssessments(parsed);
    } catch (e) {
      this._clearTriangulationData();
    }
  },

  // OTJ loading for sections that have a subject configured. Mutates the
  // controller state directly (sets otjSubject/otjLevels/etc. and the
  // shared goalsAll* lookup data).
  async _loadSectionSubjectData(students, subjectId) {
    // The caller already gates on subjectId, but guard here too — findRecord
    // with a missing id would fire a bad request.
    if (!subjectId) return;
    let subject = await this.store.findRecord('goals/subject', subjectId);
    // Everything below depends on the subject (milestones, level/expectation
    // filtering, the per-subject progression queries), so bail if it didn't
    // resolve rather than null-deref further down.
    if (!subject) return;
    let milestones = subject.get('milestones') || [];

    let [
      allLevels,
      allExpectations,
      categories,
      progressions,
      elementJudgements,
      elementEfforts,
    ] = await Promise.all([
      this.store.findAll('milestones/level'),
      this.store.findAll('milestones/expectation'),
      this.store.findAll('goals/category'),
      this.store.query('milestones/progression', {
        goalSubjectId: subjectId,
        type: ProgressionType.PROGRESSION,
        latest: true,
      }),
      this.store.query('milestones/progression', {
        goalSubjectId: subjectId,
        type: ProgressionType.PROGRESSION,
        elements: true,
      }),
      this.store.query('milestones/progression', {
        goalSubjectId: subjectId,
        type: ProgressionType.PROGRESSION,
        elements: true,
        effort: true,
      }),
    ]);

    let levels = allLevels.filter(
      (l) => l.belongsTo('goalSubjectId').id() === subjectId,
    );
    let expectations = allExpectations.filter(
      (e) => e.belongsTo('goalSubjectId').id() === subjectId,
    );
    let sortedLevels = levels
      .slice()
      .sort(
        (a, b) =>
          a.sequence - b.sequence ||
          (a.label || '').localeCompare(b.label || ''),
      );
    let showAllColours = this.options.schoolGet(
      this.session.school,
      Options.CHARTS_SHOW_ALL_COLOURS,
    );
    let expectationData = generateExpectationData(
      expectations,
      milestones,
      sortedLevels,
      showAllColours,
    );

    let milestoneMap = {};
    (students || []).forEach((student) => {
      milestoneMap[student.get('id')] = getCurrentMilestone(
        milestones,
        student,
        this.dates.rolloverMonthDate,
      );
    });

    let progressionMap = {};
    (students || []).forEach((student) => {
      progressionMap[student.get('id')] = [];
    });
    progressions.forEach((p) => {
      let personId =
        p.person_id ||
        (p.belongsTo &&
          p.belongsTo('personId') &&
          p.belongsTo('personId').id());
      if (personId && progressionMap[personId]) {
        progressionMap[personId].push(p);
      }
    });

    // Each student's strand and overall judgements and efforts from every
    // milestone and year; the progress panel picks the latest per strand.
    let judgementsByStudent = progressionsByStudent(elementJudgements);
    let effortsByStudent = progressionsByStudent(elementEfforts);
    let elementJudgementHistory = {};
    let elementEffortHistory = {};
    (students || []).forEach((student) => {
      let studentId = student.get('id');
      elementJudgementHistory[studentId] = judgementsByStudent[studentId] || [];
      elementEffortHistory[studentId] = effortsByStudent[studentId] || [];
    });

    this.setProperties({
      otjSubject: subject,
      otjLevels: sortedLevels,
      otjExpectations: expectationData,
      otjProgressions: progressionMap,
      otjMilestoneMap: milestoneMap,
      elementJudgementHistory,
      elementEffortHistory,
      goalsAllLevels: allLevels,
      goalsCategories: categories,
      goalsAllExpectations: allExpectations,
    });
  },

  // Curriculum + Current Goals loading. Driven by triangulationSubjectIds
  // configured on the section — independent of the section's own subject.
  async _loadTriangulationGoals(students, parsed, loadSectionId) {
    let triangulationSubjectIds =
      (parsed && parsed.triangulationSubjectIds) || [];
    if (!triangulationSubjectIds.length) {
      this.setProperties({
        goalsLearnerSummaries: null,
        goalsSubjects: null,
        goalsCurrentStatesMap: null,
      });
      return;
    }
    let groupController = this.groupController;
    let groupId = groupController.get('selectedGroup.id');

    // Curriculum panel: learner summaries + goal subjects (the panel consumes
    // both). Isolated so a failure here doesn't block Current Goals below,
    // which doesn't depend on either.
    try {
      let schoolId = this.session.activeSchool;
      let summaryPromises = triangulationSubjectIds.reduce((promises, sid) => {
        promises[sid] = this.parentController
          .get('heroApi')
          .goals.listLearnerSummaries(schoolId, groupId, null, sid);
        return promises;
      }, {});
      let allSummaries = await hash(summaryPromises);
      this.set('goalsLearnerSummaries', remapSummariesByLearner(allSummaries));

      let goalSubjects = await Promise.all(
        triangulationSubjectIds.map((sid) =>
          this.store.findRecord('goals/subject', sid),
        ),
      );
      this.set('goalsSubjects', goalSubjects);
    } catch (e) {
      this.setProperties({
        goalsLearnerSummaries: null,
        goalsSubjects: null,
        curriculumLoadError: true,
      });
    }

    // Current Goals panel: developing goal states. Independent of the
    // curriculum data above.
    try {
      let goalStatesMap = {};
      (students || []).forEach((s) => {
        goalStatesMap[s.get('id')] = [];
      });
      await Promise.all(
        triangulationSubjectIds.map(async (sid) => {
          let states = await this.store.query('goals/state', {
            groupId,
            subjectId: sid,
            current: true,
            include: 'goals',
          });
          states.forEach((s) => {
            if (s.get('awarded') === GoalState.DEVELOPING) {
              let pid = s.belongsTo('personId').id();
              if (goalStatesMap[pid]) {
                goalStatesMap[pid].push(s);
              }
            }
          });
        }),
      );
      this.set('goalsCurrentStatesMap', goalStatesMap);
      this._autoPopulateNextSteps(students, goalStatesMap, loadSectionId);
    } catch (e) {
      this.setProperties({
        goalsCurrentStatesMap: null,
        currentGoalsLoadError: true,
      });
    }
  },

  // Assessment-panel loading. Driven by triangulationAssessmentIds
  // configured on the section — independent of the section's own subject.
  async _loadTriangulationAssessments(parsed) {
    let triangulationAssessmentIds =
      (parsed && parsed.triangulationAssessmentIds) || [];
    if (!triangulationAssessmentIds.length) {
      this.setProperties({
        assessmentAssessments: null,
        assessmentModules: null,
        assessmentGradesMap: null,
        assessmentTimeframes: null,
      });
      return;
    }
    try {
      let [allAssessments, allModules, grades, timeframes] = await Promise.all([
        this.store.findAll('assessments/assessment'),
        this.store.findAll('assessments/module'),
        this.store.query('assessments/grade', {
          groupId: this.groupController.get('selectedGroup.id'),
          assessmentIds: JSON.stringify(triangulationAssessmentIds),
        }),
        this.store.query('timeframe', { schoolId: this.session.activeSchool }),
        // Grade schedules are read via module.gradeSchedule.awardMethod when
        // rendering each cell — preload them so cells render on first paint
        // instead of after a section switch warms the proxies.
        this.store.findAll('assessments/grade-schedule'),
      ]);

      let selectedAssessments = triangulationAssessmentIds
        .map((id) => allAssessments.find((assessment) => assessment.id === id))
        .filter(Boolean);

      this.setProperties({
        assessmentAssessments: selectedAssessments,
        assessmentModules: allModules,
        assessmentGradesMap: mapGradesByLearner(grades),
        assessmentTimeframes: timeframes
          .slice()
          .sort((a, b) => a.startDateUnix - b.startDateUnix),
      });
    } catch (e) {
      this.setProperties({
        assessmentAssessments: null,
        assessmentModules: null,
        assessmentGradesMap: null,
        assessmentTimeframes: null,
        assessmentsLoadError: true,
      });
    }
  },

  _clearOtjData() {
    this.setProperties({
      otjSubject: null,
      otjLevels: null,
      otjExpectations: null,
      otjProgressions: null,
      otjMilestoneMap: null,
      elementJudgementHistory: null,
      elementEffortHistory: null,
    });
  },

  _clearTriangulationData() {
    this.setProperties({
      otjSubject: null,
      otjLevels: null,
      otjExpectations: null,
      otjProgressions: null,
      otjMilestoneMap: null,
      elementJudgementHistory: null,
      elementEffortHistory: null,
      goalsAllLevels: null,
      goalsCategories: null,
      goalsAllExpectations: null,
      goalsLearnerSummaries: null,
      goalsSubjects: null,
      goalsCurrentStatesMap: null,
      assessmentAssessments: null,
      assessmentModules: null,
      assessmentGradesMap: null,
      assessmentTimeframes: null,
    });
  },

  _autoPopulateNextSteps(students, goalStatesMap, loadSectionId) {
    let sectionMap = this.studentReportSectionMap;
    let sectionId = loadSectionId;
    // Bail if the user switched sections while the triangulation load was in
    // flight — applying these goals would cross-contaminate the new section.
    if (!sectionId || sectionId !== this.get('activeSection.id')) return;
    let hasNextSteps = (this.activeSectionTextComponents || []).some(
      (c) => c.type === 'next-steps',
    );
    if (!hasNextSteps || !sectionMap) return;

    let changed = false;
    (students || []).forEach((student) => {
      let studentId = student.get('id');
      let states = goalStatesMap[studentId];
      if (!states || !states.length) return;
      let studentSections = sectionMap[studentId];
      if (!studentSections) return;
      let section = studentSections[sectionId];
      if (!section) return;
      if (hasText(section.get('nextStepsGoals'))) {
        return;
      }
      // Drafts saved before goals had their own field kept them in
      // nextSteps as list items, so skip goals already listed there
      let listed = listItemTexts(section.get('nextSteps'));
      let items = states
        .map((s) => {
          let goal = s.get('goal');
          return goal ? goal.get('peopleDescription') : null;
        })
        .filter((desc) => desc && !isListed(desc, listed));
      if (!items.length) return;
      // Goal peopleDescription is stored HTML-encoded, so embed as-is —
      // escaping again would double-encode entities like &#39;.
      let html =
        '<ul>' +
        items.map((desc) => '<li>' + desc + '</li>').join('') +
        '</ul>';
      section.set('nextStepsGoals', html);
      debounce(section, saveSection, section, this.toaster, 1000);
      changed = true;
    });

    if (changed) {
      this.notifyPropertyChange('studentReportSectionMap');
    }
  },

  // Register a window beforeunload handler that flushes the active TinyMCE
  // editor's content and immediately saves any dirty student-report-section
  // records. This prevents data loss when a user reloads the page while an
  // editor is focused and the debounced section.save() hasn't fired yet.
  _registerBeforeUnloadSave() {
    if (this._writeBeforeUnloadHandler) return;
    this._writeBeforeUnloadHandler = () => {
      if (this.isDestroyed || this.isDestroying) return;
      this.clickToEditCoordinator.flushActive();
      this.store
        .peekAll('reports/student-report-section')
        .forEach((section) => {
          if (section.get('hasDirtyAttributes') && !section.get('isSaving')) {
            saveSection(section, this.toaster);
          }
        });
    };
    window.addEventListener('beforeunload', this._writeBeforeUnloadHandler);
  },

  _deregisterBeforeUnloadSave() {
    if (this._writeBeforeUnloadHandler) {
      window.removeEventListener(
        'beforeunload',
        this._writeBeforeUnloadHandler,
      );
      this._writeBeforeUnloadHandler = null;
    }
  },

  // Settle everything the write view has in flight, so a read that follows
  // (the group hub's count endpoints) sees the finished state. Same two steps
  // as the beforeunload handler — push the focused editor's content into its
  // record, then save every dirty or in-flight section — but awaitable.
  //
  // Callers MUST await this before transitioning out of a write view. Section
  // saves are debounced by 1000ms, so leaving immediately fires the counts
  // request ahead of the pending PATCH and the hub renders pre-edit numbers,
  // with nothing scheduled to re-fetch afterwards.
  flushPendingSectionSaves() {
    if (this.clickToEditCoordinator) {
      this.clickToEditCoordinator.flushActive();
    }
    // Scoped to this view's own sections, not to the whole store. Section
    // records outlive the route, so a store-wide scan would also pick up one
    // left dirty by an earlier report whose save failed, retry it here, and
    // raise that report's error toast while the user is leaving this one.
    //
    // The beforeunload handler above deliberately does scan the whole store:
    // there, the page itself is going away, so unsaved work anywhere is about
    // to be lost and is worth one last attempt.
    //
    // studentReportSectionMap is the complete set for the view. It is built
    // from every section belonging to the reports the view loaded, and nothing
    // creates section records lazily, so nothing editable here can be missing
    // from it.
    let map = this.studentReportSectionMap || {};
    let sections = [];
    Object.keys(map).forEach((studentId) => {
      let studentSections = map[studentId] || {};
      Object.keys(studentSections).forEach((templateSectionId) => {
        let section = studentSections[templateSectionId];
        if (section) sections.push(section);
      });
    });

    let pending = sections
      .filter(
        (section) =>
          section.get('hasDirtyAttributes') || section.get('isSaving'),
      )
      // queueSectionSave (inside saveSection) chains onto any save already
      // running for the same record, so awaiting this also awaits that one.
      .map((section) => saveSection(section, this.toaster));
    return Promise.all(pending);
  },

  _updateWriteJudgement(student, elementId, value) {
    let section = this.getStudentReportSection(
      student.get('id'),
      this.activeSection.get('id'),
    );
    let parent = this.parentController;
    // Capture subject now so saveReportProgression uses the correct subject
    // even if the user switches sections before the 1-second timer fires.
    let subject = this.activeSectionSubject;

    if (section) {
      if (!elementId) {
        section.set('judgement', String(value));
        section.set('complete', false);
        parent.saveReportProgression(
          student,
          {
            judgement: value,
            effort: null,
            elementId: null,
          },
          subject,
        );
      } else if (value === 'na') {
        let elementProgressions = section.get('elementProgressions');
        let existing = findBy(elementProgressions, 'elementId', elementId);
        if (existing) {
          existing.set('judgement', 'na');
        } else {
          elementProgressions.createFragment({ elementId, judgement: 'na' });
        }
        let { total, count } = this._rubricElementTotals(
          elementProgressions,
          student,
          'judgement',
        );
        if (count > 0) {
          let avg = Math.round(total / count);
          section.set('judgement', String(avg));
          parent.saveReportProgression(
            student,
            { judgement: avg, effort: null, elementId: null },
            subject,
          );
        }
        parent.saveReportProgression(
          student,
          { judgement: 0, effort: null, elementId },
          subject,
        );
        section.set('complete', false);
      } else if (value === null) {
        let elementProgressions = section.get('elementProgressions');
        let existing = findBy(elementProgressions, 'elementId', elementId);
        if (existing) {
          existing.set('judgement', null);
        }
        let { total, count } = this._rubricElementTotals(
          elementProgressions,
          student,
          'judgement',
        );
        if (count > 0) {
          let avg = Math.round(total / count);
          section.set('judgement', String(avg));
          parent.saveReportProgression(
            student,
            { judgement: avg, effort: null, elementId: null },
            subject,
          );
        } else {
          section.set('judgement', null);
        }
        section.set('complete', false);
      } else {
        let elementProgressions = section.get('elementProgressions');
        let hasAnyValue =
          section.get('judgement') ||
          elementProgressions.any((ep) => ep.get('judgement'));

        if (!hasAnyValue) {
          let studentProgressions =
            (this.otjProgressions && this.otjProgressions[student.get('id')]) ||
            [];
          hasAnyValue = studentProgressions.some((p) => p.get('judgement'));
        }

        if (!hasAnyValue) {
          let rubricElements = this._getActiveRubricElements(student);
          rubricElements.forEach((el) => {
            let elId = el.get('id');
            let ep = findBy(elementProgressions, 'elementId', elId);
            if (ep) {
              ep.set('judgement', String(value));
            } else {
              elementProgressions.createFragment({
                elementId: elId,
                judgement: String(value),
              });
            }
            parent.saveReportProgression(
              student,
              {
                judgement: value,
                effort: null,
                elementId: elId,
              },
              subject,
            );
          });
          section.set('judgement', String(value));
          parent.saveReportProgression(
            student,
            {
              judgement: value,
              effort: null,
              elementId: null,
            },
            subject,
          );
        } else {
          let existing = findBy(elementProgressions, 'elementId', elementId);
          if (existing) {
            existing.set('judgement', String(value));
          } else {
            elementProgressions.createFragment({
              elementId,
              judgement: String(value),
            });
          }

          let { total, count } = this._rubricElementTotals(
            elementProgressions,
            student,
            'judgement',
          );
          if (count > 0) {
            let avg = Math.round(total / count);
            section.set('judgement', String(avg));
            parent.saveReportProgression(
              student,
              {
                judgement: avg,
                effort: null,
                elementId: null,
              },
              subject,
            );
          }

          parent.saveReportProgression(
            student,
            {
              judgement: value,
              effort: null,
              elementId: elementId,
            },
            subject,
          );
        }

        section.set('complete', false);
      }

      debounce(section, saveSection, section, this.toaster, 1000);
    }
  },

  _updateWriteEffort(student, elementId, value) {
    let section = this.getStudentReportSection(
      student.get('id'),
      this.activeSection.get('id'),
    );
    let parent = this.parentController;
    // Capture subject now so saveReportProgression uses the correct subject
    // even if the user switches sections before the 1-second timer fires.
    let subject = this.activeSectionSubject;

    if (section) {
      if (!elementId) {
        section.set('effort', String(value));
        section.set('complete', false);
        parent.saveReportProgression(
          student,
          {
            judgement: null,
            effort: value,
            elementId: null,
          },
          subject,
        );
      } else if (value === 'na') {
        let elementProgressions = section.get('elementProgressions');
        let existing = findBy(elementProgressions, 'elementId', elementId);
        if (existing) {
          existing.set('effort', 'na');
        } else {
          elementProgressions.createFragment({ elementId, effort: 'na' });
        }
        let { total, count } = this._rubricElementTotals(
          elementProgressions,
          student,
          'effort',
        );
        if (count > 0) {
          let avg = Math.round(total / count);
          section.set('effort', String(avg));
          parent.saveReportProgression(
            student,
            { judgement: null, effort: avg, elementId: null },
            subject,
          );
        }
        parent.saveReportProgression(
          student,
          { judgement: null, effort: 0, elementId },
          subject,
        );
        section.set('complete', false);
      } else if (value === null) {
        let elementProgressions = section.get('elementProgressions');
        let existing = findBy(elementProgressions, 'elementId', elementId);
        if (existing) {
          existing.set('effort', null);
        }
        let { total, count } = this._rubricElementTotals(
          elementProgressions,
          student,
          'effort',
        );
        if (count > 0) {
          let avg = Math.round(total / count);
          section.set('effort', String(avg));
          parent.saveReportProgression(
            student,
            { judgement: null, effort: avg, elementId: null },
            subject,
          );
        } else {
          section.set('effort', null);
        }
        section.set('complete', false);
      } else {
        let elementProgressions = section.get('elementProgressions');
        let hasAnyValue =
          section.get('effort') ||
          elementProgressions.any((ep) => ep.get('effort'));

        if (!hasAnyValue) {
          let studentProgressions =
            (this.otjProgressions && this.otjProgressions[student.get('id')]) ||
            [];
          hasAnyValue = studentProgressions.some((p) => p.get('effort'));
        }

        if (!hasAnyValue) {
          let rubricElements = this._getActiveRubricElements(student);
          rubricElements.forEach((el) => {
            let elId = el.get('id');
            let ep = findBy(elementProgressions, 'elementId', elId);
            if (ep) {
              ep.set('effort', String(value));
            } else {
              elementProgressions.createFragment({
                elementId: elId,
                effort: String(value),
              });
            }
            parent.saveReportProgression(
              student,
              {
                judgement: null,
                effort: value,
                elementId: elId,
              },
              subject,
            );
          });
          section.set('effort', String(value));
          parent.saveReportProgression(
            student,
            {
              judgement: null,
              effort: value,
              elementId: null,
            },
            subject,
          );
        } else {
          let existing = findBy(elementProgressions, 'elementId', elementId);
          if (existing) {
            existing.set('effort', String(value));
          } else {
            elementProgressions.createFragment({
              elementId,
              effort: String(value),
            });
          }

          let { total, count } = this._rubricElementTotals(
            elementProgressions,
            student,
            'effort',
          );
          if (count > 0) {
            let avg = Math.round(total / count);
            section.set('effort', String(avg));
            parent.saveReportProgression(
              student,
              {
                judgement: null,
                effort: avg,
                elementId: null,
              },
              subject,
            );
          }

          parent.saveReportProgression(
            student,
            {
              judgement: null,
              effort: value,
              elementId: elementId,
            },
            subject,
          );
        }

        section.set('complete', false);
      }

      debounce(section, saveSection, section, this.toaster, 1000);
    }
  },

  _updateWriteSectionField(student, componentType, value) {
    let section = this.getStudentReportSection(
      student.get('id'),
      this.activeSection.get('id'),
    );
    if (!section) return;
    let field = this.COMPONENT_FIELD_MAP[componentType];
    if (!field) return;
    // A manual edit stops slider changes regenerating this field (until the
    // teacher regenerates it or clears it).
    section.setProperties(manualEditProperties(field, value));
    if (section.get('complete')) {
      section.set('complete', false);
    }
    // reporting/get-section-field helper only re-computes when its argument
    // reference changes, so replace the map with a shallow clone to push the
    // updated field value through to preview bindings.
    this.set(
      'studentReportSectionMap',
      Object.assign({}, this.studentReportSectionMap),
    );
    debounce(section, saveSection, section, this.toaster, 1000);
  },

  // Apply rubric-generated comments from a slider change, leaving any field
  // the teacher has edited alone.
  _autoPopulateWriteSection(student, populateData) {
    let section = this.getStudentReportSection(
      student.get('id'),
      this.activeSection.get('id'),
    );
    if (!section) return;

    if (applyRubricComments(section, populateData, false)) {
      debounce(section, saveSection, section, this.toaster, 1000);
      // reporting/get-section-field only re-computes when its argument
      // reference changes, so replace the map to refresh the editors.
      this.set(
        'studentReportSectionMap',
        Object.assign({}, this.studentReportSectionMap),
      );
    }
  },

  // Replace one field with freshly generated rubric text, confirming first
  // when that would discard the teacher's edits.
  _regenerateWriteSectionField(student, componentType) {
    let section = this.getStudentReportSection(
      student.get('id'),
      this.activeSection.get('id'),
    );
    if (!section) return;

    let populateData = generateRubricCommentsForSubject({
      store: this.store,
      subject: this.activeSectionSubject,
      student,
      reportTemplate: this.activeReport,
      section,
      rolloverMonthDate: this.dates.rolloverMonthDate,
      rubricFamilyId: this.activeSectionRubricFamilyId,
    });
    // The field is missing only when no criteria match the judgements. An
    // empty field is a valid result: it clears stale generated text.
    if (!(componentType in populateData)) {
      this.toaster.error(
        'Nothing to generate yet. Set strand judgements on the rubric first.',
      );
      return;
    }
    if (
      !canAutoPopulate(section, componentType) &&
      !window.confirm(
        'Replace this text with new wording from the rubric? Your changes will be lost.',
      )
    ) {
      return;
    }

    // The other generated fields the teacher hasn't edited follow the new
    // pick too, so the comment and lists still match
    applyRubricComments(section, populateData, false);
    applyRubricComments(
      section,
      { [componentType]: populateData[componentType] },
      true,
    );
    if (section.get('complete')) {
      section.set('complete', false);
    }
    debounce(section, saveSection, section, this.toaster, 1000);
    this.set(
      'studentReportSectionMap',
      Object.assign({}, this.studentReportSectionMap),
    );
  },

  _otjChartData(records) {
    let milestones = this.otjSubject ? this.otjSubject.get('milestones') : [];
    return generateProgressData(milestones, this.otjLevels, records);
  },

  // Re-run the triangulation load for the current section/students after a
  // panel failed to load. Driven by the inline error indicators' Retry
  // button.
  retryTriangulation: action(function () {
    this._loadTriangulationData(this._triangulationStudents);
  }),
});
